import { useCallback, useRef, useSyncExternalStore } from 'react'
import type * as Y from 'yjs'
import {
  DEFAULT_BACKGROUND,
  DEFAULT_THEME,
  isFontKey,
  PALETTE_SLOTS,
  Y_META,
  type BoardTheme,
} from '@praesto/shared'

/**
 * Board-weite Einstellungen liegen im Yjs-Dokument, nicht in Postgres:
 * Hintergrund, Palette und Schriften sind Inhalt, den zwei Leute gleichzeitig
 * ändern können — und dann sollen sie sich wie jede andere Änderung verhalten.
 */
const metaMap = (doc: Y.Doc): Y.Map<unknown> => doc.getMap<unknown>(Y_META)

function subscribeToMeta(doc: Y.Doc) {
  const map = metaMap(doc)
  return (onChange: () => void) => {
    map.observe(onChange)
    return () => map.unobserve(onChange)
  }
}

export function useBoardBackground(doc: Y.Doc): string {
  const map = metaMap(doc)
  const subscribe = useCallback(subscribeToMeta(doc), [doc])
  // Der Snapshot ist eine Zeichenkette — React vergleicht mit `Object.is` und
  // rendert nur, wenn sich die Farbe wirklich ändert.
  const getSnapshot = useCallback(() => {
    const value = map.get('background')
    return typeof value === 'string' && value ? value : DEFAULT_BACKGROUND
  }, [map])
  return useSyncExternalStore(subscribe, getSnapshot)
}

export function setBoardBackground(doc: Y.Doc, color: string): void {
  doc.transact(() => metaMap(doc).set('background', color))
}

/**
 * Das Design des Boards. Der Cache ist nötig, weil `getSnapshot` bei jedem
 * Render läuft und ein jedes Mal neu gebautes Objekt React in eine Schleife
 * schickte.
 */
export function useBoardTheme(doc: Y.Doc): BoardTheme {
  const map = metaMap(doc)
  const cache = useRef<BoardTheme>(DEFAULT_THEME)
  const stale = useRef(true)

  const subscribe = useCallback(
    (onChange: () => void) => {
      const handler = (): void => {
        stale.current = true
        onChange()
      }
      map.observe(handler)
      return () => map.unobserve(handler)
    },
    [map],
  )

  const getSnapshot = useCallback(() => {
    if (!stale.current) return cache.current
    const raw = map.get('theme') as Partial<BoardTheme> | undefined
    const palette = Array.isArray(raw?.palette)
      ? raw.palette.filter((c): c is string => typeof c === 'string').slice(0, PALETTE_SLOTS)
      : DEFAULT_THEME.palette
    cache.current = {
      palette: palette.length > 0 ? palette : DEFAULT_THEME.palette,
      headingFont: isFontKey(raw?.headingFont) ? raw.headingFont : DEFAULT_THEME.headingFont,
      bodyFont: isFontKey(raw?.bodyFont) ? raw.bodyFont : DEFAULT_THEME.bodyFont,
    }
    stale.current = false
    return cache.current
  }, [map])

  return useSyncExternalStore(subscribe, getSnapshot)
}

export function setBoardTheme(doc: Y.Doc, theme: BoardTheme): void {
  doc.transact(() => metaMap(doc).set('theme', theme))
}
